import type { AccountLogoFetchedAction } from '@Application/Branding/AccountLogo/Actions/accountLogoFetched';
import type { AccountLogoRemovedAction } from '@Application/Branding/AccountLogo/Actions/accountLogoRemoved';
import type { AccountLogoReplacedAction } from '@Application/Branding/AccountLogo/Actions/accountLogoReplaced';
import type { DisplayedLogoFetchedAction } from '@Application/Branding/DisplayedLogo/Actions/displayedLogoFetched';
import type { OrganizationLogoFetchedAction } from '@Application/Branding/OrganizationLogo/Actions/organizationLogoFetched';
import type { OrganizationLogoRemovedAction } from '@Application/Branding/OrganizationLogo/Actions/organizationLogoRemoved';
import type { OrganizationLogoReplacedAction } from '@Application/Branding/OrganizationLogo/Actions/organizationLogoReplaced';
import type { UserRevokedAction } from '@Application/Security/Authentications/Actions/userRevoked';
import LogoView from '@UI/Store/Branding/Views/LogoView';
import { createSliceReducer, registerStore } from '@verclam/synapse';

export type BrandingStore = {
	displayedLogo: LogoView | null;
	accountLogo: LogoView | null;
	organizationLogo: LogoView | null;
};

const initialState: BrandingStore = {
	displayedLogo: null,
	accountLogo: null,
	organizationLogo: null,
};

type Actions =
	| DisplayedLogoFetchedAction
	| AccountLogoFetchedAction
	| AccountLogoReplacedAction
	| AccountLogoRemovedAction
	| OrganizationLogoFetchedAction
	| OrganizationLogoReplacedAction
	| OrganizationLogoRemovedAction
	| UserRevokedAction;

const whenDisplayedLogoFetched = (
	store: BrandingStore,
	action: DisplayedLogoFetchedAction,
): BrandingStore => ({
	...store,
	displayedLogo: new LogoView(action.organizationId, action.payload.dataUrl),
});

const whenAccountLogoFetched = (store: BrandingStore, action: AccountLogoFetchedAction): BrandingStore => ({
	...store,
	accountLogo: new LogoView(action.accountId, action.payload.dataUrl),
});

const whenOrganizationLogoFetched = (
	store: BrandingStore,
	action: OrganizationLogoFetchedAction,
): BrandingStore => ({
	...store,
	organizationLogo: new LogoView(action.organizationId, action.payload.dataUrl),
});

// Any structure may inherit the account logo, so the displayed logo is dropped whatever its owner.
const whenAccountLogoChanged = (
	store: BrandingStore,
	_action: AccountLogoReplacedAction | AccountLogoRemovedAction,
): BrandingStore => ({
	...store,
	accountLogo: null,
	displayedLogo: null,
});

const whenOrganizationLogoChanged = (
	store: BrandingStore,
	action: OrganizationLogoReplacedAction | OrganizationLogoRemovedAction,
): BrandingStore => {
	const { organizationId } = action.payload;

	return {
		...store,
		organizationLogo: store.organizationLogo?.ownerId === organizationId ? null : store.organizationLogo,
		displayedLogo: store.displayedLogo?.ownerId === organizationId ? null : store.displayedLogo,
	};
};

const whenUserRevoked = (): BrandingStore => initialState;

export default registerStore<'brandingStore', Actions>(
	'brandingStore',
	createSliceReducer({
		DISPLAYED_LOGO_FETCHED: whenDisplayedLogoFetched,
		ACCOUNT_LOGO_FETCHED: whenAccountLogoFetched,
		ACCOUNT_LOGO_REPLACED: whenAccountLogoChanged,
		ACCOUNT_LOGO_REMOVED: whenAccountLogoChanged,
		ORGANIZATION_LOGO_FETCHED: whenOrganizationLogoFetched,
		ORGANIZATION_LOGO_REPLACED: whenOrganizationLogoChanged,
		ORGANIZATION_LOGO_REMOVED: whenOrganizationLogoChanged,
		USER_REVOKED: whenUserRevoked,
	}),
	initialState,
);
